import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, StatusBadge, ClientLogo } from "@/components/Layout";
import AnnouncementsBanner from "@/components/AnnouncementsBanner";
import AnnouncementsManager from "@/components/AnnouncementsManager";
import PortfolioOverview from "@/components/PortfolioOverview";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Clock, CheckCircle2, Calendar, AlertTriangle, FileText, Check } from "lucide-react";
import { toast } from "sonner";
import { parseApiError } from "@/lib/apiErrors";
import { fmtDate, fmtDateTime } from "@/lib/dates";
export default function Dashboard() {
  const { user } = useAuth();
  const [todayAtt, setTodayAtt] = useState(null);
  const [balance, setBalance] = useState({});
  const [myTasks, setMyTasks] = useState([]);
  const [myProjects, setMyProjects] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [holidaysUpcoming, setHolidaysUpcoming] = useState([]);
  const [reminders, setReminders] = useState([]);

  const load = async () => {
    try {
      const [att, bal, tks, prjs, hols, cls, rems] = await Promise.all([
        api.get("/attendance/today"),
        api.get("/leave/balance"),
        api.get("/tasks", { params: { mine_only: true } }),
        api.get("/projects"),
        api.get("/holidays", { params: { year: new Date().getFullYear() } }),
        api.get("/clients"),
        api.get("/dashboard/reminders").catch(() => ({ data: [] })),
      ]);
      setTodayAtt(att.data);
      setBalance(bal.data || {});
      setMyTasks(tks.data || []);
      setAllProjects(prjs.data || []);
      const mine = (prjs.data || []).filter((p) => (p.assignee_ids || []).includes(user.user_id));
      setMyProjects(mine);
      setClients(cls.data || []);
      setReminders(rems.data || []);
      const today = new Date().toISOString().slice(0, 10);
      const monthPrefix = today.slice(0, 7); // YYYY-MM
      setHolidaysUpcoming(
        (hols.data || [])
          .filter((h) => h.date >= today && (h.date || "").startsWith(monthPrefix))
          .slice(0, 5)
      );
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const handleReminder = async (r) => {
    // Optimistic dismiss — feels instant. If the request fails, restore.
    const dedupeKey = `${r.item_id}-${r.reminder_column_key || ""}-${r.reminder_date}`;
    setReminders((rs) => rs.filter((x) =>
      `${x.item_id}-${x.reminder_column_key || ""}-${x.reminder_date}` !== dedupeKey,
    ));
    try {
      await api.post("/dashboard/reminders/handle", {
        item_id: r.item_id,
        column_key: r.reminder_column_key,
        reminder_date: r.reminder_date,
      });
      toast.success(`Marked “${r.label}” as handled`);
    } catch (e) {
      // Revert on failure.
      setReminders((rs) => [...rs, r].sort((a, b) => a.reminder_date.localeCompare(b.reminder_date)));
      toast.error(parseApiError(e, "Couldn’t mark as handled"));
    }
  };

  const captureGeo = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve({});
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
      () => resolve({}),
      { enableHighAccuracy: true, timeout: 6000, maximumAge: 60000 }
    );
  });

  const checkin = async () => {
    try {
      const geo = await captureGeo();
      await api.post("/attendance/checkin", geo);
      toast.success(geo.lat ? "Checked in (location captured)" : "Checked in");
      load();
    } catch (e) {
      toast.error(parseApiError(e, "Check-in failed"));
    }
  };

  const checkout = async () => {
    try {
      const geo = await captureGeo();
      await api.post("/attendance/checkout", geo);
      toast.success(geo.lat ? "Checked out (location captured)" : "Checked out");
      load();
    } catch (e) {
      toast.error(parseApiError(e, "Check-out failed"));
    }
  };

  const pendingTasks = myTasks.filter((t) => t.status !== "Completed");
  const projectById = (id) => allProjects.find((p) => p.id === id);
  const clientById = (id) => clients.find((c) => c.id === id);

  return (
    <div className="min-h-screen bg-white" data-testid="dashboard-page">
      <PageHeader
        title={`Good ${greeting()}, ${user.name?.split(" ")[0] || "there"}.`}
        subtitle={new Date().toLocaleDateString("en-IN", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
        action={
          (user.role === "Admin" || user.role === "Manager") && <AnnouncementsManager />
        }
      />

      <div className="px-4 sm:px-8 pt-6">
        <AnnouncementsBanner />
      </div>

      <PortfolioOverview allProjects={allProjects} clients={clients} user={user} />

      <div className="px-4 sm:px-8 py-6 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {/* Attendance */}
        <Card className="p-5 border-slate-200 shadow-none rounded-sm" data-testid="card-attendance">
          <div className="gc-label">Attendance</div>
          <div className="mt-3 text-xs font-mono-data text-slate-500">
            {todayAtt?.check_in
              ? `Checked in at ${new Date(todayAtt.check_in).toLocaleTimeString()}`
              : "Not checked in"}
          </div>
          <div className="mt-1 text-xs font-mono-data text-slate-500">
            {todayAtt?.check_out
              ? `Checked out at ${new Date(todayAtt.check_out).toLocaleTimeString()}`
              : ""}
          </div>
          <div className="mt-4 flex gap-2">
            {!todayAtt?.check_in && (
              <Button size="sm" className="bg-[#0a2540] hover:bg-[#0f2f50] rounded-sm" onClick={checkin} data-testid="btn-checkin">
                <Clock size={14} className="mr-1.5" /> Check in
              </Button>
            )}
            {todayAtt?.check_in && !todayAtt?.check_out && (
              <Button size="sm" variant="outline" className="rounded-sm" onClick={checkout} data-testid="btn-checkout">
                <CheckCircle2 size={14} className="mr-1.5" /> Check out
              </Button>
            )}
            {todayAtt?.check_in && todayAtt?.check_out && (
              <span className="status-pill status-completed">Done</span>
            )}
          </div>
        </Card>

        {/* Leave balance */}
        <Card className="p-5 border-slate-200 shadow-none rounded-sm md:col-span-2" data-testid="card-leave-balance">
          <div className="gc-label">Leave Balance</div>
          <div className="mt-3 grid grid-cols-4 gap-3">
            {Object.entries(balance).map(([k, v]) => (
              <div key={k} className="border border-slate-200 px-3 py-2">
                <div className="text-[10px] gc-label truncate">{k}</div>
                <div className="text-2xl font-mono-data font-medium text-[#0a2540]">{v}</div>
              </div>
            ))}
          </div>
          <div className="mt-3">
            <Link to="/leave" className="text-xs text-[#0a2540] underline" data-testid="link-apply-leave">
              Apply for leave →
            </Link>
          </div>
        </Card>

        {/* Holidays */}
        <Card className="p-5 border-slate-200 shadow-none rounded-sm" data-testid="card-holidays">
          <div className="gc-label">Holidays This Month</div>
          <ul className="mt-3 space-y-1.5">
            {holidaysUpcoming.length === 0 && <li className="text-xs text-slate-400">None this month</li>}
            {holidaysUpcoming.map((h) => (
              <li key={h.id} className="flex items-center gap-2 text-xs">
                <Calendar size={12} className="text-slate-400" />
                <span className="font-mono-data text-slate-600">{fmtDate(h.date)}</span>
                <span className="text-slate-700 truncate">{h.name}</span>
              </li>
            ))}
          </ul>
        </Card>

        {/* Reminders this month (from compliance DBs with reminder columns) */}
        <Card className="p-5 border-slate-200 shadow-none rounded-sm md:col-span-2 lg:col-span-3" data-testid="card-reminders">
          <div className="flex items-center justify-between">
            <div className="gc-label">Reminders This Month</div>
            <span className="text-[10px] text-slate-400 font-mono-data">{reminders.length} item{reminders.length === 1 ? "" : "s"}</span>
          </div>
          {reminders.length === 0 ? (
            <div className="mt-3 text-xs text-slate-400">No reminders this month.</div>
          ) : (
            <div className="mt-3 divide-y divide-slate-200 border border-slate-200">
              {reminders.map((r) => (
                <div
                  key={`${r.item_id}-${r.reminder_column_key || ""}-${r.reminder_date}`}
                  className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 text-xs group"
                  data-testid={`reminder-${r.item_id}`}
                >
                  <span
                    className={`shrink-0 px-1.5 py-0.5 rounded-sm text-[10px] font-semibold font-mono-data ${
                      r.days_until < 0
                        ? "bg-red-50 text-red-700 border border-red-200"
                        : r.is_today
                          ? "bg-red-50 text-red-700 border border-red-200"
                          : r.days_until <= 3
                            ? "bg-amber-50 text-amber-700 border border-amber-200"
                            : "bg-slate-50 text-slate-600 border border-slate-200"
                    }`}
                    title={
                      r.days_until < 0
                        ? `${Math.abs(r.days_until)} day${Math.abs(r.days_until) === 1 ? "" : "s"} overdue`
                        : r.is_today
                          ? "Due today"
                          : `${r.days_until} day${r.days_until === 1 ? "" : "s"} away`
                    }
                  >
                    {r.is_today
                      ? "TODAY"
                      : r.days_until < 0
                        ? `${Math.abs(r.days_until)}d overdue`
                        : r.days_until === 1
                          ? "Tomorrow"
                          : `in ${r.days_until}d`}
                  </span>
                  <span className="font-mono-data text-slate-500 shrink-0 w-20">{fmtDate(r.reminder_date)}</span>
                  <Link
                    to={`/clients/${r.client_id}`}
                    className="flex-1 min-w-0 truncate font-medium text-slate-800 hover:text-[#0a2540] hover:underline"
                  >
                    {r.label}
                  </Link>
                  <span className="text-slate-500 truncate hidden sm:inline">· {r.client_name}</span>
                  <span className="text-[10px] text-slate-400 italic hidden md:inline truncate">{r.db_name}</span>
                  <button
                    type="button"
                    onClick={() => handleReminder(r)}
                    className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] text-slate-500 border border-transparent hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700 rounded-sm opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                    title="Mark this reminder as handled"
                    data-testid={`reminder-handle-${r.item_id}`}
                  >
                    <Check size={11} /> <span className="hidden sm:inline">Mark handled</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* My pending tasks */}
        <Card className="p-5 border-slate-200 shadow-none rounded-sm md:col-span-2 lg:col-span-3" data-testid="card-my-tasks">
          <div className="flex items-center justify-between">
            <div className="gc-label">My Pending Tasks</div>
            <Link to="/my-tasks" className="text-xs text-[#0a2540] underline" data-testid="link-all-tasks">
              View all →
            </Link>
          </div>
          <div className="mt-3 divide-y divide-slate-200 border border-slate-200">
            {pendingTasks.length === 0 && (
              <div className="px-3 py-6 text-center text-xs text-slate-400">No pending tasks</div>
            )}
            {pendingTasks.slice(0, 6).map((t) => {
              const proj = projectById(t.project_id);
              const cli = proj ? clientById(proj.client_id) : null;
              return (
                <Link
                  key={t.id}
                  to={`/projects/${t.project_id}`}
                  className="flex items-center justify-between px-3 py-2.5 hover:bg-slate-50 gap-3"
                  data-testid={`task-row-${t.id}`}
                >
                  <div className="flex items-start gap-2 min-w-0 flex-1">
                    {cli && <ClientLogo fileId={cli.logo_file_id} name={cli.name} size={28} />}
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#0f172a] truncate">{t.title}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                        {cli?.name && <span className="font-mono-data">{cli.name}</span>}
                        {cli?.name && proj?.title && <span className="mx-1.5 text-slate-300">·</span>}
                        {proj?.title}
                      </div>
                      <div className="text-[10px] text-slate-400 font-mono-data mt-0.5">Due {fmtDate(t.due_date)}</div>
                    </div>
                  </div>
                  <StatusBadge status={t.status} />
                </Link>
              );
            })}
          </div>
        </Card>

        {/* My projects */}
        <Card className="p-5 border-slate-200 shadow-none rounded-sm md:col-span-1" data-testid="card-my-projects">
          <div className="gc-label">My Projects</div>
          <ul className="mt-3 space-y-2">
            {myProjects.length === 0 && (
              <li className="text-xs text-slate-400">No projects assigned</li>
            )}
            {myProjects.slice(0, 5).map((p) => {
              const cli = clientById(p.client_id);
              return (
                <li key={p.id}>
                  <Link
                    to={`/projects/${p.id}`}
                    className="block border border-slate-200 px-3 py-2 hover:border-[#0a2540]"
                    data-testid={`project-row-${p.id}`}
                  >
                    <div className="flex items-start gap-2">
                      {cli && <ClientLogo fileId={cli.logo_file_id} name={cli.name} size={20} />}
                      <div className="min-w-0 flex-1">
                        <div className="text-xs font-medium text-[#0f172a] truncate">{p.title}</div>
                        <div className="text-[10px] text-slate-400 truncate">{cli?.name}</div>
                      </div>
                    </div>
                    <div className="mt-1.5 flex items-center justify-between gap-2">
                      <StatusBadge status={p.status} />
                      <span className="text-[10px] font-mono-data text-slate-500">{p.progress_pct ?? 0}%</span>
                    </div>
                    <div className="mt-1 h-1 bg-slate-100">
                      <div className="h-full bg-[#0a2540]" style={{ width: `${p.progress_pct ?? 0}%` }} />
                    </div>
                    {p.stuck_with && p.stuck_with !== "None" && (
                      <div className="mt-1 text-[10px] text-amber-700 inline-flex items-center gap-1">
                        <AlertTriangle size={10} /> Stuck: {p.stuck_with}
                      </div>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>
    </div>
  );
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  return "evening";
}
